/**
 * Shared auth-page chrome — brand mark, gradient backdrop, centered
 * card, and a subtle background grid. Used by SignIn, ForgotPassword,
 * ResetPassword, and the TOTP prompt so the surface stays consistent.
 *
 * Pass an `icon` so the TOTP step can show ShieldCheck and the reset-success
 * step can show CheckCircle2. Without an `icon` the Vloud logo is shown.
 */
import { type ReactNode, type JSX } from 'react';
import { type LucideIcon } from 'lucide-react';

export function AuthShell({
  icon: Icon,
  title,
  subtitle,
  children,
  footer,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  return (
    <div className="auth-surface" style={{ position: 'relative' }}>
      <BackgroundGlow />
      <div
        className="auth-card"
        style={{
          position: 'relative',
          // The base styles.css owns the .auth-card layout; we only
          // layer the polish on top so legacy callers still render
          // correctly.
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(99, 102, 241, 0.08) inset',
        }}
      >
        {Icon ? <BrandMark icon={Icon} /> : <BrandLogo />}
        <h1 style={{ textAlign: 'center', marginBottom: 4 }}>{title}</h1>
        {subtitle && (
          <p
            className="subtitle"
            style={{ textAlign: 'center', margin: '0 auto 16px', maxWidth: 360 }}
          >
            {subtitle}
          </p>
        )}
        {children}
        {footer && (
          <div
            style={{
              marginTop: 18, paddingTop: 14,
              borderTop: '1px solid var(--border)',
              fontSize: 12, textAlign: 'center',
              color: 'var(--text-muted)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function BrandLogo(): JSX.Element {
  // The logo's wordmark is navy, so it sits on a white tile on this dark surface.
  return (
    <img
      src="/vloud-logo-white.png"
      alt="Vloud - Cloud, Simplified."
      width={132}
      height={137}
      style={{
        display: 'block', width: 132, height: 'auto', margin: '0 auto 14px',
        borderRadius: 20, boxShadow: '0 12px 32px rgba(99, 102, 241, 0.30)',
      }}
    />
  );
}

function BrandMark({ icon: Icon }: { icon: LucideIcon }): JSX.Element {
  return (
    <div
      aria-hidden
      style={{
        width: 60, height: 60,
        borderRadius: 18,
        margin: '0 auto 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.30), rgba(167, 139, 250, 0.18))',
        border: '1px solid rgba(99, 102, 241, 0.55)',
        boxShadow:
          '0 12px 32px rgba(99, 102, 241, 0.30), 0 0 0 6px rgba(99, 102, 241, 0.06)',
        color: '#c4b5fd',
        position: 'relative',
      }}
    >
      <Icon width={28} height={28} />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: -6,
          borderRadius: 22,
          border: '1px dashed rgba(167, 139, 250, 0.25)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function BackgroundGlow(): JSX.Element {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99, 102, 241, 0.22), transparent 60%),' +
          ' radial-gradient(ellipse 70% 50% at 80% 100%, rgba(167, 139, 250, 0.16), transparent 60%),' +
          ' radial-gradient(ellipse 70% 50% at 10% 90%, rgba(34, 211, 238, 0.10), transparent 60%)',
      }}
    />
  );
}
